import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle, RotateCcw } from 'lucide-react';
import { FormSchema, FormField, ConditionalLogic } from '../../types/form';
import { formsApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type FormData = Record<string, any>;
type FormErrors = Record<string, string>;

// ─── Conditional Logic Evaluator ─────────────────────────────────────────────

function evaluateConditions(conditions: ConditionalLogic[], formData: FormData): boolean {
  return conditions.every(({ field, operator, value, action }) => {
    const fieldVal = formData[field];
    let result = false;
    switch (operator) {
      case 'equals':        result = String(fieldVal) === String(value); break;
      case 'not_equals':    result = String(fieldVal) !== String(value); break;
      case 'contains':      result = String(fieldVal ?? '').includes(String(value)); break;
      case 'greater_than':  result = Number(fieldVal) > Number(value); break;
      case 'less_than':     result = Number(fieldVal) < Number(value); break;
      case 'is_empty':      result = !fieldVal || fieldVal === ''; break;
      case 'is_not_empty':  result = !!fieldVal && fieldVal !== ''; break;
      default:              result = false;
    }
    return action === 'show' ? result : !result;
  });
}

function isFieldVisible(field: FormField, formData: FormData): boolean {
  if (!field.conditionalLogic || field.conditionalLogic.length === 0) return true;
  return evaluateConditions(field.conditionalLogic, formData);
}

// ─── Validation ───────────────────────────────────────────────────────────────

function validateField(field: FormField, value: any): string {
  const v = field.validation;
  const isEmpty = value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  if (field.required && isEmpty) {
    return v?.customMessage || `${field.label} is required`;
  }
  if (isEmpty) return '';

  const strVal = String(value);
  if (v?.minLength && strVal.length < v.minLength)
    return `Minimum ${v.minLength} characters required`;
  if (v?.maxLength && strVal.length > v.maxLength)
    return `Maximum ${v.maxLength} characters allowed`;
  if (v?.min !== undefined && Number(value) < v.min)
    return `Minimum value is ${v.min}`;
  if (v?.max !== undefined && Number(value) > v.max)
    return `Maximum value is ${v.max}`;
  if (v?.pattern) {
    try {
      if (!new RegExp(v.pattern).test(strVal))
        return v.patternMessage || 'Invalid format';
    } catch { /* ignore bad regex */ }
  }
  return '';
}

// ─── Field Components ─────────────────────────────────────────────────────────

interface FieldProps {
  field: FormField;
  value: any;
  error?: string;
  onChange: (name: string, value: any) => void;
}

function FieldInput({ field, value, error, onChange }: FieldProps) {
  const base =
    'w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ' +
    (error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white');

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          id={field.name}
          className={`${base} resize-y min-h-[100px]`}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={e => onChange(field.name, e.target.value)}
          rows={4}
        />
      );

    case 'select':
      return (
        <select
          id={field.name}
          className={base}
          value={value ?? ''}
          onChange={e => onChange(field.name, e.target.value)}
        >
          <option value="">{field.placeholder || 'Select an option'}</option>
          {field.options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.name}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(field.name, opt.value)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{opt.label}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="space-y-2">
          {field.options?.map(opt => {
            const checked = Array.isArray(value) ? value.includes(opt.value) : false;
            return (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => {
                    const current: string[] = Array.isArray(value) ? [...value] : [];
                    onChange(
                      field.name,
                      e.target.checked
                        ? [...current, opt.value]
                        : current.filter(v => v !== opt.value)
                    );
                  }}
                  className="text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            );
          })}
        </div>
      );

    case 'file':
      return (
        <input
          id={field.name}
          type="file"
          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          onChange={e => onChange(field.name, e.target.files?.[0] ?? null)}
          accept={field.validation?.fileTypes?.join(',')}
        />
      );

    case 'signature':
      return (
        <div className={`${base} h-24 flex items-center justify-center text-gray-400 text-sm`}>
          ✍️ Signature field (canvas integration available)
        </div>
      );

    default:
      return (
        <input
          id={field.name}
          type={field.type}
          className={base}
          placeholder={field.placeholder}
          value={value ?? ''}
          onChange={e => onChange(field.name, e.target.value)}
        />
      );
  }
}

function FormFieldRow({ field, value, error, onChange }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1 ${field.width === 'half' ? 'md:col-span-1' : field.width === 'third' ? 'md:col-span-1' : 'col-span-full'}`}>
      <label htmlFor={field.name} className="text-sm font-medium text-gray-700">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {field.helpText && (
        <p className="text-xs text-gray-500">{field.helpText}</p>
      )}
      <FieldInput field={field} value={value} error={error} onChange={onChange} />
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const DynamicFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Read prefill values from query params
  const prefillValues = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill: Record<string, string> = {};
    params.forEach((value, key) => prefill[key] = value);
    return prefill;
  }, []);

  // Fetch form schema
  useEffect(() => {
    if (!id) { setError('No form ID provided'); setLoading(false); return; }
    const numId = Number(id);
    if (isNaN(numId)) { setError('Invalid form ID'); setLoading(false); return; }

    formsApi.getById(numId)
      .then(data => {
        setSchema(data);
        // Build initial formData from defaultValues + prefill
        const allFields = data.isMultiStep
          ? data.steps.flatMap(s => s.fields)
          : data.fields;
        const initial: FormData = {};
        allFields.forEach(f => {
          if (prefillValues[f.name] !== undefined) {
            initial[f.name] = prefillValues[f.name];
          } else if (f.defaultValue !== undefined) {
            initial[f.name] = f.defaultValue;
          }
        });
        // Also restore draft from localStorage
        const draft = localStorage.getItem(`form_draft_${numId}`);
        if (draft) {
          try { Object.assign(initial, JSON.parse(draft)); } catch { /* ignore */ }
        }
        setFormData(initial);
      })
      .catch(err => setError(err?.response?.data?.message || 'Failed to load form'))
      .finally(() => setLoading(false));
  }, [id]);

  const allFields: FormField[] = schema
    ? (schema.isMultiStep ? schema.steps.flatMap(s => s.fields) : schema.fields)
    : [];

  const handleChange = useCallback((name: string, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Save draft
      if (id) localStorage.setItem(`form_draft_${id}`, JSON.stringify(next));
      return next;
    });
    // Clear error on change
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  }, [id]);

  const validateAll = (): boolean => {
    const errors: FormErrors = {};
    allFields.forEach(field => {
      if (!isFieldVisible(field, formData)) return;
      const err = validateField(field, formData[field.name]);
      if (err) errors[field.name] = err;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) return;

    setSubmitting(true);
    const payload = {
      formId: id,
      data: formData,
      submittedAt: new Date().toISOString(),
    };
    console.log('Form submission payload:', payload);

    // Attempt real API submission, fall back to mock
    try {
      await formsApi.submit(Number(id), { data: formData });
    } catch {
      // Mock success if API not available
      await new Promise(r => setTimeout(r, 600));
    }

    localStorage.removeItem(`form_draft_${id}`);
    setSubmitted(true);
    setSubmitting(false);
  };

  const handleReset = () => {
    setFormData({});
    setFormErrors({});
    setSubmitted(false);
    if (id) localStorage.removeItem(`form_draft_${id}`);
  };

  // ── Render states ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Form Not Available</h2>
          <p className="text-gray-500 text-sm">{error || 'The form you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Submitted!</h2>
          <p className="text-gray-500 text-sm mb-6">
            {schema.thankYouMessage || 'Thank you for your submission.'}
          </p>
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Submit another response
          </button>
        </div>
      </div>
    );
  }

  // ── Form render ────────────────────────────────────────────────────────────

  const visibleFields = allFields.filter(f => isFieldVisible(f, formData));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
          <h1 className="text-2xl font-bold text-gray-900">{schema.title}</h1>
          {schema.description && (
            <p className="text-gray-500 text-sm mt-1">{schema.description}</p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {visibleFields.map(field => (
                <FormFieldRow
                  key={field.id}
                  field={field}
                  value={formData[field.name]}
                  error={formErrors[field.name]}
                  onChange={handleChange}
                />
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DynamicFormPage;
